import React from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { selectRole, selectUserName } from '../redux/auth/auth.selectors';
import { authLoginReset } from '../redux/auth/auth.action';
import { selectLang } from '../redux/session/session.selectors';
import { setLang, sessionReset } from '../redux/session/session.action';
import { httpTismsApi } from '../api/httpTismsApi';
import type { SystemNotification } from '../data/tisms-api';
import { styles } from './TopBar.styles';

/* §2.1 Top bar (56px). Logo + brand + live dot + signed-in role + lang toggle
   + live audit notifications + user block + logout. */
export const TopBar: React.FC = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const role = useSelector(selectRole);
  const userName = useSelector(selectUserName);
  const lang = useSelector(selectLang);

  const [notifications, setNotifications] = React.useState<SystemNotification[]>([]);
  const [showNotifs, setShowNotifs] = React.useState(false);
  const dropdownRef = React.useRef<HTMLDivElement>(null);

  const loadNotifications = React.useCallback(async () => {
    try {
      const data = await httpTismsApi.getNotifications();
      setNotifications(data || []);
    } catch {
      /* notifications table may not be polled if unauthenticated */
    }
  }, []);

  React.useEffect(() => {
    loadNotifications();
    const timer = setInterval(loadNotifications, 30000); // refresh every 30s
    return () => clearInterval(timer);
  }, [loadNotifications]);

  // Close dropdown on outside click
  React.useEffect(() => {
    const handleOutsideClick = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setShowNotifs(false);
      }
    };
    if (showNotifs) {
      document.addEventListener('mousedown', handleOutsideClick);
    }
    return () => {
      document.removeEventListener('mousedown', handleOutsideClick);
    };
  }, [showNotifs]);

  const unreadCount = notifications.filter((n) => !n.is_read).length;

  const onLogout = (): void => {
    dispatch(authLoginReset());
    dispatch(sessionReset());
    navigate('/');
  };

  const handleNotificationClick = async (notif: SystemNotification) => {
    try {
      await httpTismsApi.markNotificationRead(notif.notification_id);
      setNotifications((prev) =>
        prev.map((n) => (n.notification_id === notif.notification_id ? { ...n, is_read: 1 } : n))
      );
    } catch {
      /* ignore */
    }
    if (notif.report_ref) {
      setShowNotifs(false);
      navigate(notif.report_ref);
    }
  };

  return (
    <header style={styles.header}>
      <img
        src="/tnsc-logo.jpg"
        alt="TNSC"
        width={32}
        height={32}
        style={styles.logo}
      />
      <div style={styles.brandTitle}>
        TNSC Bank <span style={styles.brandDot}>•</span> TISMS
      </div>

      <div style={styles.spacer} />

      {/* Live status */}
      <div style={styles.liveStatus}>
        <span style={styles.pulseDot} />
        <span style={styles.liveText}>All changes saved</span>
      </div>

      {/* Live Audit Notifications Bell */}
      <div ref={dropdownRef} style={{ position: 'relative' }}>
        <button
          onClick={() => setShowNotifs((v) => !v)}
          title="Audit Progress & Notifications"
          style={styles.topBtn}
        >
          <span>🔔</span>
          <span style={{ fontSize: 11.5 }}>Audits</span>
          {unreadCount > 0 && (
            <span style={styles.bellBadge}>{unreadCount > 9 ? '9+' : unreadCount}</span>
          )}
        </button>

        {showNotifs && (
          <div style={styles.notifDropdown}>
            <div style={styles.notifHeader}>
              <span style={styles.notifHeaderTitle}>Live Audit Progress & Notifications</span>
              <span style={{ fontSize: 11, color: '#888' }}>
                {unreadCount} unread
              </span>
            </div>
            {notifications.length === 0 ? (
              <div style={styles.notifEmpty}>No audit notifications yet.</div>
            ) : (
              <ul style={styles.notifList}>
                {notifications.map((n) => (
                  <li
                    key={n.notification_id}
                    onClick={() => handleNotificationClick(n)}
                    style={{
                      ...styles.notifItem,
                      background: n.is_read ? '#fff' : 'rgba(15,81,50,0.04)',
                      cursor: n.report_ref ? 'pointer' : 'default',
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      <span style={styles.notifItemTitle}>{n.title}</span>
                      <span style={{ fontSize: 10, color: '#999' }}>
                        {n.created_at ? n.created_at.slice(11, 16) : ''}
                      </span>
                    </div>
                    <div style={styles.notifItemMessage}>{n.message}</div>
                    {n.stage ? (
                      <div style={styles.notifItemStage}>
                        Stage: {n.stage} ({n.progress_pct}%)
                      </div>
                    ) : null}
                    <div style={styles.notifProgressBarTrack}>
                      <div
                        style={{
                          height: '100%',
                          width: `${Math.min(100, Math.max(0, n.progress_pct))}%`,
                          background: n.progress_pct >= 100 ? '#198754' : '#0F5132',
                        }}
                      />
                    </div>
                    {n.report_ref && (
                      <span style={styles.notifActionLink}>
                        Open Live Inspection Report →
                      </span>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}
      </div>

      {/* Role */}
      <span style={styles.roleBadge}>
        {role?.name ?? '—'}
      </span>

      {/* Language toggle */}
      <button
        onClick={() => dispatch(setLang(lang === 'ta' ? 'en' : 'ta'))}
        title="Toggle bilingual"
        style={styles.topBtn}
      >
        {lang === 'ta' ? 'த | EN' : 'EN'}
      </button>

      {/* User block */}
      <div style={styles.userBlock}>
        <div style={styles.userNameText}>{userName}</div>
        <div style={styles.userScopeText}>{role?.scope}</div>
      </div>

      {/* Password change */}
      <button
        onClick={() => navigate('/account/password')}
        title="Change your password"
        style={styles.topBtn}
      >
        Password
      </button>

      {/* Logout */}
      <button
        onClick={onLogout}
        style={styles.logoutBtn}
      >
        Logout
      </button>
    </header>
  );
};

export default TopBar;
